import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { materialRequestSchema, getFirstError } from "@/lib/validations";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function MaterialRequest() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [intent, setIntent] = useState<"sell" | "source">("source");
  const [description, setDescription] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    setOpen(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = materialRequestSchema.safeParse({ email, intent, description, gdprConsent });
    if (!result.success) {
      toast({ title: getFirstError(result), variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("material_requests").insert({
        email: result.data.email,
        intent: result.data.intent,
        material_description: result.data.description,
      });
      if (error) throw error;
      supabase.functions.invoke("send-material-request-confirmation", {
        body: {
          email: result.data.email,
          intent: result.data.intent,
          description: result.data.description,
        },
      }).catch(console.error);
      setIsSubmitted(true);
      setEmail("");
      setIntent("source");
      setDescription("");
      setGdprConsent(false);
    } catch {
      toast({ title: t("home.requestError"), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value && isSubmitted) {
      setTimeout(() => setIsSubmitted(false), 300);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Post a Material Request — HIDNstock</title>
        <meta name="description" content="Sell or source deadstock, sorted secondhand or remake material — HIDNstock matches you with the right B2B partner." />
        <link rel="canonical" href="https://hidnstock.com/request" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/request" />
        <meta property="og:title" content="Sell or Source Material — HIDNstock" />
        <meta property="og:description" content="Tell us what textile material you have or need — we'll match you with brands, sorting facilities or redesigners." />
      </Helmet>
      <section className="py-24 min-h-[80vh] flex items-center justify-center">
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold tracking-tight">
                {t("home.requestTitle")}
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground">
                {t("home.requestSubtitle")}
              </DialogDescription>
            </DialogHeader>
            {isSubmitted ? (
              <div className="py-8 text-center">
                <p className="text-3xl font-bold mb-2">{t("home.requestSuccess")}</p>
                <p className="text-muted-foreground">{t("home.requestSuccessDesc")}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Select
                  value={intent}
                  onValueChange={(value) => setIntent(value as "sell" | "source")}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("home.requestIntentLabel")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="source">{t("home.requestIntentSource")}</SelectItem>
                    <SelectItem value="sell">{t("home.requestIntentSell")}</SelectItem>
                  </SelectContent>
                </Select>

                <Textarea
                  placeholder={t("home.requestPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[80px] resize-none"
                  rows={3}
                />
                <Input
                  type="email"
                  placeholder={t("home.requestEmailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="request-gdpr"
                    checked={gdprConsent}
                    onCheckedChange={(checked) => setGdprConsent(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="request-gdpr" className="text-muted-foreground text-xs leading-tight cursor-pointer">
                    {t("home.requestGdpr")}
                  </label>
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full font-bold text-base">
                  {isSubmitting ? "..." : t("home.requestButton")}
                </Button>
                <p className="text-muted-foreground text-xs text-center">{t("home.requestDisclaimer")}</p>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </section>
    </Layout>
  );
}
